import { PatientCreateCCSPicker } from '@chiroup/components/body/PatientCreateCCSPicker';
import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React, { useMemo, useState } from 'react';
import InteractiveComplaintSummary from '../../../../patients/detail/visits/notes/InteractiveComplaintSummary';
import DiscoveryPopup from '../components/DiscoveryPopup';
import HowCollectedModal from '../components/HowCollectedModal';
import VoiceToChartDemoModal from '../components/VoiceToChartDemoModal';
import WalkthroughCallout from '../components/WalkthroughCallout';
import {
  buildAnitaCcsSections,
  SANDBOX_COMPLAINT,
  SANDBOX_INTAKE_SURVEYS,
  SANDBOX_QUESTION_ANSWER_MAP,
} from '../fixtures/anitaBack';
import { HOW_COLLECTED_FLYOUT, SUBJECTIVE_COPY, SUBJECTIVE_EDIT_POPUP } from '../sandboxCopy';

interface SubjectiveStepProps {
  onDone: () => void;
}

type PopupKind = 'edit' | 'howCollected' | 'voiceToChart' | `survey:${string}` | null;

const noop = () => undefined;

/**
 * Step 1 — Review Subjective. The completed Chief Complaint Survey is the
 * hero content, rendered through the same buildFlexibleSections pipeline the
 * real Subjective step uses. Edit affordances stay visible (spec: "standard 2
 * edit icons"), but every edit interaction is intercepted into the
 * "Need to make a change?" discovery pop-up instead of mutating the summary.
 */
const SubjectiveStep: React.FC<SubjectiveStepProps> = ({ onDone }) => {
  const [popup, setPopup] = useState<PopupKind>(null);

  const sections = useMemo(() => buildAnitaCcsSections(), []);

  // Capture-phase interception: any interactive element inside the summary
  // (edit pencils, green answer links, popover triggers) opens the discovery
  // pop-up; plain text/scroll interactions pass through untouched.
  const interceptEdits = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, a, [role="button"], svg, .cursor-pointer')) {
      e.preventDefault();
      e.stopPropagation();
      setPopup('edit');
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      <div className="flex-1 rounded-lg border border-gray-200 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {SANDBOX_COMPLAINT.complaint}
            </h2>
            <p className="text-xs text-gray-400">Complaint 1 of 1</p>
          </div>
          {/* Mirrors the idle RecorderLive bar (mic tile / timer / complete
              tile) so the sandbox control looks like the real EHR's; the whole
              control opens the Voice-to-Chart demonstration. */}
          <button
            type="button"
            onClick={() => setPopup('voiceToChart')}
            aria-label="AI Voice to Chart demonstration"
            className="group flex shrink-0 flex-col items-center"
          >
            <span className="flex w-40 sm:w-44 items-center justify-between gap-2 rounded-lg border border-gray-300 bg-gray-100 p-1 transition-colors group-hover:border-primary-400 dark:border-gray-700 dark:bg-gray-800">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md border border-gray-300 bg-white transition-colors group-hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-700 dark:group-hover:bg-gray-600">
                <Icon
                  icon={Icons.microphone}
                  className="h-5 w-5 text-gray-700 dark:text-gray-200"
                />
              </span>
              <span className="grow text-center text-xl font-bold tabular-nums text-gray-700 dark:text-gray-200">
                0:00
              </span>
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md border border-gray-300 bg-gray-200 text-gray-400 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-600">
                <Icon icon={Icons.check} className="h-5 w-5" />
              </span>
            </span>
            <span className="mt-1 text-[11px] leading-tight">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                AI Voice to Chart
              </span>
              <span className="text-gray-400"> · AI visit capture</span>
            </span>
          </button>
        </div>

        {/* Same layout as the real Subjective step (SubjectiveStepV2): body
            picker in a right column at xl, above the summary on tablets;
            clicking it routes to the same "everything is editable" pop-up
            (tester note #7). Hidden on phones to keep the mobile walkthrough
            lean. */}
        <div className="mt-4 flex flex-col xl:flex-row-reverse gap-4 sm:gap-6">
          <div className="hidden md:block md:[--body-picker-max-h:400px] xl:[--body-picker-max-h:300px] xl:w-1/3 xl:min-w-0">
            <PatientCreateCCSPicker
              parts={SANDBOX_COMPLAINT.bodypicker?.parts}
              handleEditBodyPicker={() => setPopup('edit')}
              isEditable
            />
          </div>
          <div className="grow min-w-0" onClickCapture={interceptEdits}>
            <InteractiveComplaintSummary
              complaintId={SANDBOX_COMPLAINT.id as string}
              sections={sections}
              questionAnswerMaps={SANDBOX_QUESTION_ANSWER_MAP}
              onAnswerChange={noop}
              isEditable
            />
          </div>
        </div>

        <div className="mt-5 border-t border-gray-100 dark:border-dark-gray-700 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Also completed before this visit
          </p>
          {/* One balanced row: completed-form chips left, the softly colored
              "see how" pill (tester note #9) right; wraps below on narrow
              widths. */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {SANDBOX_INTAKE_SURVEYS.map(survey => (
              <button
                key={survey.id}
                type="button"
                onClick={() => setPopup(`survey:${survey.id}`)}
                className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:text-gray-900 dark:border-dark-gray-600 dark:bg-dark-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
              >
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-white">
                  <Icon icon={Icons.check} className="h-2.5 w-2.5" />
                </span>
                {survey.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:w-80 shrink-0">
        {/* The how-collected trigger lives here, not at the foot of the form
            where it was overlooked (round-3 tester note #1). */}
        <WalkthroughCallout
          stepLabel={SUBJECTIVE_COPY.stepLabel}
          title={SUBJECTIVE_COPY.title}
          body={SUBJECTIVE_COPY.body}
          cta={SUBJECTIVE_COPY.cta}
          onCta={onDone}
          secondary={HOW_COLLECTED_FLYOUT.trigger}
          onSecondary={() => setPopup('howCollected')}
          className="lg:sticky lg:top-4"
        />
      </div>

      {popup === 'edit' && (
        <DiscoveryPopup
          title={SUBJECTIVE_EDIT_POPUP.title}
          body={SUBJECTIVE_EDIT_POPUP.body}
          onDismiss={() => setPopup(null)}
        />
      )}
      {popup === 'howCollected' && <HowCollectedModal onClose={() => setPopup(null)} />}
      {popup === 'voiceToChart' && <VoiceToChartDemoModal onClose={() => setPopup(null)} />}
      {popup?.startsWith('survey:') &&
        (() => {
          const survey = SANDBOX_INTAKE_SURVEYS.find(s => `survey:${s.id}` === popup);

          return survey ? (
            <DiscoveryPopup title={survey.name} onDismiss={() => setPopup(null)}>
              {survey.paragraphs.map((paragraph, i) => (
                <p key={i} className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  {i === 0 && survey.boldLead ? (
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                      {survey.boldLead}{' '}
                    </span>
                  ) : null}
                  {paragraph}
                </p>
              ))}
              {survey.footer ? (
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  <span className="font-semibold text-gray-800 dark:text-gray-100">
                    {survey.footer.lead}{' '}
                  </span>
                  {survey.footer.text}
                </p>
              ) : null}
            </DiscoveryPopup>
          ) : null;
        })()}
    </div>
  );
};

export default SubjectiveStep;
